export type NavigationKind =
  | "home"
  | "catalog"
  | "product"
  | "info"
  | "search"
  | "account"
  | "checkout"
  | "error"
  | "generic";

const STATIC_INFO_PATHS = new Set([
  "/about_us",
  "/cookies_policy",
  "/customer_care",
  "/delivery",
  "/payment_methods",
  "/privacy_policy",
  "/refund_policy",
  "/size_guide",
  "/terms",
  "/work_with_us",
  "/contact",
]);

const CATALOG_PATH_PREFIXES = [
  "/store/category",
  "/store/manufacturer",
  "/store/search",
  "/store/special",
  "/store/catalog",
];

const normalizePath = (value = "/") => {
  const pathname = String(value || "/").split(/[?#]/, 1)[0] || "/";
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "");
};

export const isNavigationKind = (value: unknown): value is NavigationKind =>
  [
    "home",
    "catalog",
    "product",
    "info",
    "search",
    "account",
    "checkout",
    "error",
    "generic",
  ].includes(String(value));

// Catalog destinations already expose an inline skeleton while their data is
// resolving. A full-page cover hides useful route feedback and can remain
// frozen during a View Transition, so it must never own catalog navigation.
export const shouldUseRouteLoadingCover = (kind: NavigationKind) =>
  kind !== "catalog";

/**
 * Home is assembled from several async position modules. Adding a document
 * snapshot around either side of a Home/catalog navigation can leave the old
 * transition active while the next tap is already being handled. Let the
 * originating router-link (or plain anchor fallback) own these navigations so
 * one user action always produces exactly one route change.
 */
export const shouldNavigateWithoutRouteTransition = (
  currentKind: NavigationKind,
  targetKind: NavigationKind
) =>
  targetKind === "home" || (currentKind === "home" && targetKind === "catalog");

/**
 * Classifies the destination before the SEO resolver has finished. SEO catch-all
 * URLs are catalog-like by default; product cards explicitly provide a product
 * intent so their shared-image animation remains authoritative.
 */
export const classifyNavigation = ({
  pathname,
  routeName,
  explicitKind,
}: {
  pathname: string;
  routeName?: string | symbol | null;
  explicitKind?: unknown;
}): NavigationKind => {
  if (isNavigationKind(explicitKind)) return explicitKind;

  const path = normalizePath(pathname);
  if (path === "/") return "home";
  if (path === "/404") return "error";
  if (path.startsWith("/store/checkout")) return "checkout";
  if (path.startsWith("/store/product")) return "product";
  if (CATALOG_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) {
    return "catalog";
  }
  if (path === "/search" || path.startsWith("/search/")) return "search";
  if (path === "/account" || path.startsWith("/account/")) return "account";
  if (
    STATIC_INFO_PATHS.has(path) ||
    path.startsWith("/page/") ||
    path.startsWith("/blog/")
  ) {
    return "info";
  }

  // Products, categories and manufacturers all initially resolve through this
  // route. Product links supply explicitKind="product" at click time; the safe
  // neutral default for the remaining SEO destinations is a catalog transition.
  if (String(routeName || "") === "_slug") return "catalog";

  return "generic";
};

export const isQueryOnlyNavigation = (
  currentPathname: string,
  targetPathname: string
) => normalizePath(currentPathname) === normalizePath(targetPathname);

/**
 * Home is assembled from async position extensions, so its empty route shell
 * is not a reliable client-navigation readiness marker. Treat the destination
 * as ready only when the real Home content has replaced the previous page.
 */
export const isHomeDestinationReady = ({
  pathname,
  hasHeading,
  hasHomeContent,
  hasReplacedPreviousContent,
  isErrorPage,
}: {
  pathname: string;
  hasHeading: boolean;
  hasHomeContent: boolean;
  hasReplacedPreviousContent: boolean;
  isErrorPage: boolean;
}) =>
  isErrorPage ||
  (normalizePath(pathname) === "/" &&
    hasHeading &&
    hasHomeContent &&
    hasReplacedPreviousContent);

/**
 * A saved scroll position belongs to an existing browser-history entry. Never
 * apply it to a new router.push navigation, even if an embedded browser or a
 * reused history state happens to provide one.
 */
export const shouldRestoreSavedScroll = (
  isBackForwardNavigation: boolean,
  savedPosition: unknown
) => isBackForwardNavigation && Boolean(savedPosition);

/**
 * The Performance Navigation Timing entry describes how the current document
 * was opened. Its `back_forward` value remains unchanged for the document's
 * entire lifetime, so it must never classify later router.push navigations as
 * history visits.
 */
export const isInitialDocumentBackForwardNavigation = (
  navigationType: unknown,
  hasCompletedInitialNavigation: boolean
) =>
  !hasCompletedInitialNavigation &&
  String(navigationType || "") === "back_forward";
